/**
 * RTWO Event Bus — Publisher Registry Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type {
  PublisherId,
  RTWOPublisher,
  RTWOPublisherStatus,
  RTWOEventType,
} from '../domain-models';
import type { IPublisherRegistryService, PublisherRegistryResult } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { EBusInternalEventTypes } from '../events/ebus-internal-event-bus';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class PublisherRegistryService extends BaseEBusService implements IPublisherRegistryService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.PUBLISHER_REGISTRY;
  readonly serviceName = 'Publisher Registry';
  readonly description =
    'Registry of active event publishers via engine source adapter. No publishers registered.';
  readonly integrations = ['All RTWO Engines', 'Event Registry', 'Topic Registry'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async registerPublisher(
    moduleId: string,
    supportedEvents: RTWOEventType[]
  ): Promise<PublisherRegistryResult> {
    if (!this.engineSourceRegistry.isConfigured()) {
      return {
        success: false,
        publisherId: null,
        error:
          'No Publishers Registered — Cannot register publishers until real RTWO engine connections are established.',
        isPlaceholder: true,
        source: 'Event Bus — No Publishers Registered',
      };
    }

    const adapterResult = await this.engineSourceRegistry
      .getActiveAdapter()
      .registerPublisher(moduleId, supportedEvents);

    if (adapterResult?.publisher) {
      this.repositories.publishers.save(adapterResult.publisher);
      this.context.eventBus.emit(EBusInternalEventTypes.PUBLISHER_REGISTERED, {
        publisherId: adapterResult.publisher.publisherId,
        moduleId,
      });
    }

    return {
      success: false,
      publisherId: null,
      error:
        'No Publishers Registered — Cannot register publishers until real RTWO engine connections are established.',
      isPlaceholder: true,
      source: 'Event Bus — No Publishers Registered',
    };
  }

  getAllPublishers(): RTWOPublisher[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.publishers.findAll();
  }

  getPublisher(publisherId: PublisherId): RTWOPublisher | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.publishers.findById(publisherId);
  }

  getPublishersByStatus(status: RTWOPublisherStatus): RTWOPublisher[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.publishers.findByStatus(status);
  }

  getActivePublisherCount(): 0 {
    return 0;
  }
}
